import { openKlaus, trackKlausImpression } from '@app/constants/klaus';
import { NavigationSection, NavigationSubsection, Topics } from '@app/context/constants';
import { useUser } from '@app/context/UserContext';
import { useKlausPromoAllowed } from '@app/hooks/useKlausPromoAllowed';
import { useNavigation } from '@app/hooks/useNavigation';
import { LATEST_CHANGELOG_VERSION } from '@components/ui/AccountMenu/ChangelogSheet';
import { isUrgentIncident, useIncidentDeadlinesQuery } from '@dieterApi/incident/useIncidentDeadlinesQuery';
import { isUrgentRequest, useRightsRequestsQuery } from '@dieterApi/rightsRequest/useRightsRequestsQuery';
import CloseIcon from '@mui/icons-material/Close';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

type NotificationId = 'rights-deadline' | 'changelog' | 'mfa' | 'klaus';

interface NotificationEntry {
  id: NotificationId;
  dismissable: boolean;
}

export function NotificationBanner() {
  const { t } = useTranslation();
  const { user } = useUser();
  const { allowed: klausPromoAllowed, resolving: klausPromoResolving } = useKlausPromoAllowed();
  const { navigation, setNavigation, hasLoaded, navigateDashboard } = useNavigation();
  const navigate = useHistory();
  const [visible, setVisible] = useState(false);

  const hasUnseenChangelog = navigation.changelogLastSeenVersion !== LATEST_CHANGELOG_VERSION;
  const shouldShowMfa = !user?.mfaEnabled && !navigation.mfaNotificationDismissed && user?.isSubscriber;
  // Klaus is a cross-promo for our own sibling product, so it stays out of any whitelabel context
  const shouldShowKlaus = !navigation.klausBannerDismissed && klausPromoAllowed;

  // Deadline warnings are not persisted as dismissed: as long as an urgent item
  // exists, the banner returns on the next app load. Uses the same "urgent" definition
  // as the dashboard widget so both always show the same count.
  const { data: rightsRequestsData, loading: rightsRequestsLoading } = useRightsRequestsQuery();
  const { data: incidentDeadlinesData, loading: incidentDeadlinesLoading } = useIncidentDeadlinesQuery();
  const urgentRightsCount = (rightsRequestsData?.getRightsRequests || []).filter(isUrgentRequest).length;
  const urgentIncidentCount = (incidentDeadlinesData?.getIncidentDeadlines || []).filter(isUrgentIncident).length;
  const urgentDeadlineCount = urgentRightsCount + urgentIncidentCount;

  // Build the queue once, as soon as the persisted navigation state and the whitelabel resolution
  // have arrived — reading the dismissed flags before the former would resurrect banners the user
  // already closed, and building before the latter would drop Klaus for the whole session, since
  // its gate answers "no" while it does not yet know. Changelog is shown first (not dismissable),
  // then the Klaus cross-promo, then MFA.
  const [queue, setQueue] = useState<NotificationEntry[] | null>(null);
  const [queueIndex, setQueueIndex] = useState(0);

  useEffect(() => {
    if (!hasLoaded || klausPromoResolving || rightsRequestsLoading || incidentDeadlinesLoading || queue) return;
    const q: NotificationEntry[] = [];
    // Deadline warnings first - a running statutory deadline beats every promo
    if (urgentDeadlineCount > 0) q.push({ id: 'rights-deadline', dismissable: true });
    // The changelog talks about Dieter and Klaus by name: not for a whitelabel tenant.
    if (hasUnseenChangelog && klausPromoAllowed) q.push({ id: 'changelog', dismissable: false });
    // Klaus before MFA: the MFA hint stays until dismissed and would otherwise hide Klaus
    // from new customers for a long time.
    if (shouldShowKlaus) q.push({ id: 'klaus', dismissable: true });
    if (shouldShowMfa) q.push({ id: 'mfa', dismissable: true });
    setQueue(q);
  }, [
    hasLoaded,
    klausPromoResolving,
    rightsRequestsLoading,
    incidentDeadlinesLoading,
    queue,
    urgentDeadlineCount,
    hasUnseenChangelog,
    shouldShowMfa,
    shouldShowKlaus,
  ]);

  const current = queue?.[queueIndex] ?? null;

  // Fade-in after 2 s whenever a new notification becomes current
  useEffect(() => {
    if (!current) return;
    setVisible(false);
    const timer = setTimeout(() => {
      setVisible(true);
      if (current.id === 'klaus') trackKlausImpression('banner');
    }, 2000);
    return () => clearTimeout(timer);
  }, [current]);

  // Fade-out the current item, then advance to the next after the transition
  const advance = () => {
    setVisible(false);
    setTimeout(() => setQueueIndex((i) => i + 1), 500);
  };

  // The queue is frozen at build time - skip the deadline banner if all requests
  // were resolved in the meantime (e.g. marked as done in another tab)
  useEffect(() => {
    if (current?.id === 'rights-deadline' && urgentDeadlineCount === 0) advance();
  }, [current, urgentDeadlineCount]);

  const handleOpenChangelog = () => {
    setNavigation((nav) => {
      nav.changelogOpen = true;
      nav.changelogLastSeenVersion = LATEST_CHANGELOG_VERSION;
    });
    advance();
  };

  const handleMfaCta = () => {
    setNavigation((nav) => void (nav.mfaNotificationDismissed = true));
    navigate.push('/mfa');
    advance();
  };

  // Clicking through to Klaus counts as handled — no reason to keep nagging afterwards
  const handleKlausCta = () => {
    setNavigation((nav) => void (nav.klausBannerDismissed = true));
    openKlaus('banner');
    advance();
  };

  const handleRightsDeadlineCta = () => {
    if (urgentRightsCount > 0) {
      navigateDashboard(NavigationSection.Topic, Topics.RIGHTS, NavigationSubsection.Requests);
    } else {
      navigateDashboard(NavigationSection.Topic, Topics.DSV, NavigationSubsection.Incidents);
    }
    advance();
  };

  // Wording depends on which kinds of items are urgent
  const deadlineBannerText =
    urgentIncidentCount === 0
      ? urgentRightsCount === 1
        ? 'Bei einer Betroffenenanfrage läuft die Antwortfrist ab'
        : `Bei ${urgentRightsCount} Betroffenenanfragen läuft die Antwortfrist ab`
      : urgentRightsCount === 0
        ? urgentIncidentCount === 1
          ? 'Bei einem Datenschutzvorfall läuft die 72h-Meldefrist ab'
          : `Bei ${urgentIncidentCount} Datenschutzvorfällen läuft die 72h-Meldefrist ab`
        : `Bei ${urgentDeadlineCount} Datenschutz-Vorgängen laufen gesetzliche Fristen ab`;
  const deadlineBannerHint =
    urgentIncidentCount === 0
      ? ' — Art. 12 Abs. 3 DSGVO.'
      : urgentRightsCount === 0
        ? ' — Art. 33 DSGVO.'
        : ' — DSGVO.';

  const handleDismiss = () => {
    if (current.id === 'changelog') {
      setNavigation((nav) => void (nav.changelogLastSeenVersion = LATEST_CHANGELOG_VERSION));
    } else if (current.id === 'mfa') {
      setNavigation((nav) => void (nav.mfaNotificationDismissed = true));
    } else if (current.id === 'klaus') {
      setNavigation((nav) => void (nav.klausBannerDismissed = true));
    }
    advance();
  };

  if (!current) return null;

  return (
    <div
      className={cx(
        'absolute inset-0 z-10 bg-linear-to-r from-primary-50 to-blue-50 transition-all duration-500 ease-out',
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
      )}
    >
      <div className="h-full 2xl:container md:px-7 px-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          {current.id === 'rights-deadline' && (
            <>
              <span className="text-base">⏳</span>
              <span className="font-medium">{deadlineBannerText}</span>
              <span className="hidden sm:inline text-gray-500">{deadlineBannerHint}</span>
            </>
          )}
          {current.id === 'changelog' && (
            <>
              <span className="text-base">🚀</span>
              <span className="font-medium">Neue Updates verfügbar</span>
              <span className="hidden sm:inline text-gray-500"> — entdecke was sich verbessert hat.</span>
            </>
          )}
          {current.id === 'mfa' && (
            <>
              <span className="text-base">🔒</span>
              <span>{t('route.dashboard.mfanotice')}</span>
            </>
          )}
          {current.id === 'klaus' && (
            <>
              <span className="text-base">⚡</span>
              <span className="font-medium">{t('klaus.banner.title')}</span>
              <span className="hidden sm:inline text-gray-500"> {t('klaus.banner.subtitle')}</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {current.id === 'rights-deadline' && (
            <button
              onClick={handleRightsDeadlineCta}
              className="text-sm font-medium text-primary-root hover:underline whitespace-nowrap"
              data-testid="rights-deadline-banner-cta"
            >
              {urgentRightsCount > 0 ? 'Zu den Anfragen' : 'Zu den Vorfällen'} →
            </button>
          )}
          {current.id === 'changelog' && (
            <button
              onClick={handleOpenChangelog}
              className="text-sm font-medium text-primary-root hover:underline whitespace-nowrap"
            >
              Jetzt ansehen →
            </button>
          )}
          {current.id === 'mfa' && (
            <button
              onClick={handleMfaCta}
              className="text-sm font-medium text-primary-root hover:underline whitespace-nowrap"
            >
              {t('route.dashboard.mfanotice_cta')} →
            </button>
          )}
          {current.id === 'klaus' && (
            <button
              onClick={handleKlausCta}
              className="text-sm font-medium text-primary-root hover:underline whitespace-nowrap"
              data-testid="klaus-banner-cta"
            >
              {t('klaus.banner.cta')} →
            </button>
          )}
          {current.dismissable && (
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600"
              data-testid={`notification-banner-dismiss-${current.id}`}
            >
              <CloseIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
